/**
 * Policies Page - SOC 2 Policy Management
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, ChevronRight, X, Bold, Italic, Underline, List, ListOrdered, AlignLeft, History, Clock, Eye, Search } from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';
import SearchBar from '../components/common/SearchBar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Escape user-typed regex metacharacters so the highlighter never crashes
// on inputs like "POL-AC-001" or "data (sensitive)".
function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Simple Rich Text Editor Component
function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  
  // Initialize content
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      // Only set if different to avoid cursor jumping
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  return (
    <div className="rich-editor-container">
      <div className="rich-editor-toolbar">
        <button type="button" onClick={() => execCommand('bold')} title="Bold">
          <Bold size={16} />
        </button>
        <button type="button" onClick={() => execCommand('italic')} title="Italic">
          <Italic size={16} />
        </button>
        <button type="button" onClick={() => execCommand('underline')} title="Underline">
          <Underline size={16} />
        </button>
        <span className="toolbar-divider" />
        <button type="button" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
          <List size={16} />
        </button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
          <ListOrdered size={16} />
        </button>
        <span className="toolbar-divider" />
        <select 
          onChange={(e) => execCommand('formatBlock', e.target.value)} 
          defaultValue=""
          className="toolbar-select"
        >
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
        <button type="button" onClick={() => execCommand('removeFormat')} title="Clear Formatting">
          <AlignLeft size={16} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-editor-content"
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        suppressContentEditableWarning
      />
    </div>
  );
}

export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPolicies();
  }, []);

  const filteredPolicies = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return policies;
    const tokens = q.split(/\s+/).filter(Boolean);
    return policies.filter((p) => {
      const hay = [
        p.title || '',
        p.policy_id || '',
        p.category || '',
        p.summary || '',
      ].join(' ').toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  })();

  // Highlight matched terms within a piece of text.
  const highlightMatch = (text) => {
    const q = searchQuery.trim();
    if (!q || !text) return text;
    const tokens = q.split(/\s+/).filter(Boolean).map(escapeRegExp);
    if (tokens.length === 0) return text;
    const re = new RegExp(`(${tokens.join('|')})`, 'gi');
    const parts = String(text).split(re);
    return parts.map((part, i) =>
      re.test(part)
        ? (
          <mark
            key={i}
            style={{
              background: 'rgba(250, 204, 21, 0.35)',
              color: 'inherit',
              padding: '0 2px',
              borderRadius: 2,
            }}
          >
            {part}
          </mark>
        )
        : part
    );
  };

  const fetchPolicies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/policies/?limit=50`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPolicies(data.policies || []);
      }
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (policyId, format) => {
    try {
      const response = await fetch(
        `${API_URL}/api/policies/${policyId}/export/${format}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Download failed' }));
        toast.error(error.detail || 'Download failed');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${policyId}.${format}`;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${policyId}.${format}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed — check console for details');
    }
  };

  // Preview state — opens PDF inline in a modal iframe
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPolicyId, setPreviewPolicyId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreview = async (policyId) => {
    setPreviewPolicyId(policyId);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/policies/${policyId}/export/pdf`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        toast.error('Preview failed to load');
        setPreviewOpen(false);
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewPolicyId(null);
    setPreviewOpen(false);
  };

  const handleEdit = async (policy) => {
    try {
      const response = await fetch(`${API_URL}/api/policies/${policy.policy_id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const fullPolicy = await response.json();
        setSelectedPolicy(fullPolicy);
        // Content is already HTML - just use it directly
        // Clean up any excessive whitespace
        let htmlContent = (fullPolicy.content_markdown || '')
          .replace(/\n{2,}/g, '\n')  // Remove multiple newlines
          .replace(/>\s+</g, '><')    // Remove whitespace between tags
          .trim();
        setEditorContent(htmlContent);
        setEditModalOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load policy details');
    }
  };

  const handleSavePolicy = async () => {
    try {
      // Save the HTML content directly (it's already formatted)
      const response = await fetch(`${API_URL}/api/policies/${selectedPolicy.policy_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content_markdown: editorContent }),
      });
      if (response.ok) {
        toast.success('Policy updated successfully');
        setEditModalOpen(false);
        fetchPolicies();
      } else {
        toast.error('Failed to save policy');
      }
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  const handleExportAll = async (format) => {
    toast.info('Preparing download...');
    
    try {
      const response = await fetch(
        `${API_URL}/api/policies/export-all/${format}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        toast.error('Export failed');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Eyvo_SOC2_Policies.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Downloaded all policies');
    } catch (error) {
      console.error('Export all error:', error);
      toast.error('Export failed');
    }
  };

  return (
    <div className="policies-page">
      <div className="page-header">
        <div>
          <h1>Policy Management</h1>
          <p>{policies.length} SOC 2 policies — single source of truth. Edit here, export PDF/Word on demand.</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" data-testid="export-all-pdf" onClick={() => handleExportAll('pdf')}>
            <Download size={16} /> Export All as PDF
          </button>
          <button className="btn-secondary" onClick={() => handleExportAll('docx')}>
            <Download size={16} /> Export All as Word
          </button>
        </div>
      </div>

      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder='Find a policy — try "capacity management" or "POL-AC"'
        totalCount={policies.length}
        filteredCount={filteredPolicies.length}
        testIdPrefix="policy-search"
      />

      <div className="policies-list">
        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : filteredPolicies.length === 0 ? (
          <Card className="empty-state" data-testid="policy-search-empty" style={{ padding: 32, textAlign: 'center' }}>
            <Search size={32} style={{ opacity: 0.4, margin: '0 auto 12px' }} />
            <h3 style={{ margin: 0 }}>No policies match &ldquo;{searchQuery}&rdquo;</h3>
            <p style={{ marginTop: 6, color: '#94a3b8' }}>
              Try a shorter phrase, or check the spelling.
            </p>
            <button
              className="btn-secondary"
              onClick={() => setSearchQuery('')}
              style={{ marginTop: 14 }}
            >
              Clear search
            </button>
          </Card>
        ) : (
          filteredPolicies.map((policy) => (
            <Card 
              key={policy.policy_id} 
              className="policy-card clickable"
              onClick={() => handleEdit(policy)}
              data-testid={`policy-${policy.policy_id}`}
            >
              <div className="policy-id">{highlightMatch(policy.policy_id)}</div>
              <div className="policy-info">
                <div className="policy-title">{highlightMatch(policy.title)}</div>
                <div className="policy-meta">
                  v{policy.version || '1.0'} — {highlightMatch((policy.category || '').replace(/_/g, ' '))}
                </div>
              </div>
              {policy.public && <Badge color="#059669">Public</Badge>}
              <Badge color="#059669">{policy.status}</Badge>
              <button
                className="link-btn muted"
                onClick={(e) => { e.stopPropagation(); handlePreview(policy.policy_id); }}
                data-testid={`preview-${policy.policy_id}`}
                title="Preview PDF"
              >
                <Eye size={14} /> Preview
              </button>
              <button 
                className="link-btn muted" 
                onClick={(e) => { e.stopPropagation(); handleExport(policy.policy_id, 'pdf'); }}
              >
                PDF
              </button>
              <button 
                className="link-btn muted" 
                onClick={(e) => { e.stopPropagation(); handleExport(policy.policy_id, 'docx'); }}
              >
                Word
              </button>
              <ChevronRight size={16} className="policy-arrow" />
            </Card>
          ))
        )}
      </div>

      {/* Edit Policy Modal */}
      {editModalOpen && selectedPolicy && (
        <div className="modal-overlay" onClick={() => { setEditModalOpen(false); setShowRevisionHistory(false); }}>
          <div className="modal-content policy-editor" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedPolicy.policy_id}: {selectedPolicy.title}</h2>
              <button className="modal-close" onClick={() => { setEditModalOpen(false); setShowRevisionHistory(false); }}>
                <X size={20} />
              </button>
            </div>
            
            {/* Tab Navigation */}
            <div className="policy-tabs">
              <button 
                className={`policy-tab ${!showRevisionHistory ? 'active' : ''}`}
                onClick={() => setShowRevisionHistory(false)}
              >
                Edit Policy
              </button>
              <button 
                className={`policy-tab ${showRevisionHistory ? 'active' : ''}`}
                onClick={() => setShowRevisionHistory(true)}
              >
                <History size={16} /> Revision History
              </button>
            </div>
            
            <div className="modal-body">
              {!showRevisionHistory ? (
                <>
                  <div className="policy-meta-info">
                    <span>Version: {selectedPolicy.version}</span>
                    <span>Owner: {selectedPolicy.owner}</span>
                    <span>Status: {selectedPolicy.status}</span>
                  </div>
                  <RichTextEditor
                    value={editorContent}
                    onChange={setEditorContent}
                  />
                </>
              ) : (
                <div className="revision-history">
                  <div className="revision-header">
                    <h3>Policy Revision History</h3>
                    <p className="revision-subtitle">Audit trail of all changes to this policy</p>
                  </div>
                  {selectedPolicy.revision_history && selectedPolicy.revision_history.length > 0 ? (
                    <div className="revision-timeline">
                      {[...selectedPolicy.revision_history].reverse().map((revision, index) => (
                        <div key={index} className="revision-item">
                          <div className="revision-marker">
                            <div className="revision-dot" />
                            {index < selectedPolicy.revision_history.length - 1 && <div className="revision-line" />}
                          </div>
                          <div className="revision-content">
                            <div className="revision-version">
                              <span className="version-badge">v{revision.version}</span>
                              {index === 0 && <span className="current-badge">Current</span>}
                            </div>
                            <div className="revision-date">
                              <Clock size={14} />
                              {new Date(revision.date).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <div className="revision-changes">{revision.changes}</div>
                            <div className="revision-author">Modified by: {revision.author}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-revisions">
                      <p>No revision history available for this policy.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!showRevisionHistory ? (
                <>
                  <button className="btn-secondary" onClick={() => { setEditModalOpen(false); setShowRevisionHistory(false); }}>Cancel</button>
                  <button 
                    className="btn-primary" 
                    onClick={handleSavePolicy}
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <button className="btn-secondary" onClick={() => { setEditModalOpen(false); setShowRevisionHistory(false); }}>Close</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview PDF Modal */}
      {previewOpen && (
        <div className="modal-overlay" onClick={closePreview} data-testid="preview-pdf-modal">
          <div
            className="modal-content pdf-preview-modal"
            onClick={e => e.stopPropagation()}
            style={{ width: '90vw', maxWidth: '1100px', height: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <h2>
                <Eye size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                PDF Preview {previewPolicyId ? `— ${previewPolicyId}` : ''}
              </h2>
              <button className="modal-close" onClick={closePreview} data-testid="preview-pdf-close">
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
              {previewLoading && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: '#6b7280', fontSize: 14,
                }}>
                  Generating PDF preview…
                </div>
              )}
              {previewUrl && (
                <iframe
                  title="PDF Preview"
                  src={previewUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  data-testid="preview-pdf-iframe"
                />
              )}
            </div>
            <div className="modal-actions" style={{ marginTop: 12 }}>
              {previewUrl && previewPolicyId && (
                <button
                  className="btn-primary"
                  onClick={() => handleExport(previewPolicyId, 'pdf')}
                  data-testid="preview-pdf-download"
                >
                  <Download size={16} /> Download PDF
                </button>
              )}
              <button className="btn-secondary" onClick={closePreview}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
